// UserDetailPage.jsx - Comprehensive User Analytics View
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/ui/layout/app-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, User, Mail, Calendar, Shield, Building, Users, 
  FileText, ShoppingCart, UserPlus, Package, FileCheck, 
  Receipt, Truck, CreditCard, Signature, DollarSign,
  Activity, BarChart3, TrendingUp, Loader2, RefreshCw,
  CheckCircle, XCircle, Clock, AlertTriangle
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BASE_URL } from '../lib/api';
export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);

 

  // Fetch user analytics data
  const fetchUserAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('superadmin_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Fetching user analytics for:', userId);

      const response = await fetch(`${BASE_URL}/user-analytics/user/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Super admin privileges required.');
        }
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        }
        if (response.status === 404) {
          throw new Error('User not found.');
        }
        throw new Error(`Failed to fetch user data: ${response.status}`);
      }

      const data = await response.json();
      console.log('User Analytics Response:', data);
      
      setUserAnalytics(data);
      
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      setError(error.message);
      
      // If authentication failed, redirect to login
      if (error.message.includes('Authentication failed')) {
        localStorage.removeItem('superadmin_token');
        localStorage.removeItem('admin_user');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserAnalytics();
    } else {
      setError('No user ID provided');
      setLoading(false);
    }
  }, [userId]);

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getUserInitials = (fullName) => {
    if (!fullName) return 'U';
    const names = fullName.split(' ');
    if (names.length >= 2) {
      return names[0][0].toUpperCase() + names[1][0].toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  const getUserTypeColor = (userType) => {
    switch(userType) {
      case 'owner': return 'bg-purple-500';
      case 'team_member': return 'bg-blue-500';
      case 'superadmin': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Create chart data for document distribution
  const createDocumentChartData = (documentCounts) => {
    if (!documentCounts) return [];
    
    const data = [
      { name: 'Invoices', value: documentCounts.invoices, color: '#8884d8' },
      { name: 'Order Forms', value: documentCounts.orderForms, color: '#82ca9d' },
      { name: 'Customers', value: documentCounts.customers, color: '#ffc658' },
      { name: 'Credit Notes', value: documentCounts.creditNotes, color: '#ff7300' },
      { name: 'Delivery Challans', value: documentCounts.deliveryChallans, color: '#00ff00' },
      { name: 'E-Signatures', value: documentCounts.eSignatureRequests, color: '#ff00ff' }
    ].filter(item => item.value > 0); // Only show items with data
    
    return data;
  };

  // Create template chart data
  const createTemplateChartData = (documentCounts) => {
    if (!documentCounts) return [];
    
    return [
      { name: 'Terms Templates', count: documentCounts.termsTemplates },
      { name: 'MSA Templates', count: documentCounts.msaTemplates },
      { name: 'Cover Page Templates', count: documentCounts.coverPageTemplates }
    ];
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading user analytics...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <AlertTriangle className="h-12 w-12 mx-auto mb-2" />
            <h2 className="text-xl font-semibold">Error Loading User Data</h2>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-2">
            <Button onClick={() => fetchUserAnalytics()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          
          </div>
        </div>
      </div>
    );
  }

  if (!userAnalytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <User className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p className="text-gray-600">No user data available</p>
        </div>
      </div>
    );
  }

  const { user, documentCounts } = userAnalytics;
  const chartData = createDocumentChartData(documentCounts);
  const templateData = createTemplateChartData(documentCounts);

  return (
    <>
      <AppHeader title={`User Analytics - ${user.fullName || 'Unknown User'}`} showBackButton={true} backButtonPath="/dashboard">
        <div className="flex items-center gap-4">
          <Button onClick={() => fetchUserAnalytics()} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>

        </div>
      </AppHeader>

      <main className="flex-1 p-4 md:p-6 space-y-6">
        {/* User Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className={`w-20 h-20 rounded-full ${getUserTypeColor(user.userType)} flex items-center justify-center text-xl font-bold text-white`}>
                {getUserInitials(user.fullName)}
              </div>
              
              {/* User Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{user.fullName || 'No Name Provided'}</h1>
                  <Badge variant={user.emailConfirmed ? 'default' : 'destructive'}>
                    {user.emailConfirmed ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Unverified
                      </>
                    )}
                  </Badge>
                  <Badge variant="outline" className="capitalize">
                    {user.userType?.replace('_', ' ') || 'Unknown'}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{user.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>ID: {user.id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                 <span>Joined: {formatDate(user.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document Statistics Overview */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardDescription>Total Documents</CardDescription>
              <Activity className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{documentCounts?.totalDocuments || 0}</div>
              <p className="text-xs text-muted-foreground">All created documents</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardDescription>Business Documents</CardDescription>
              <FileText className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(documentCounts?.invoices || 0) + (documentCounts?.orderForms || 0)}
              </div>
              <p className="text-xs text-muted-foreground">Invoices + Order Forms</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardDescription>Templates Created</CardDescription>
              <Package className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(documentCounts?.termsTemplates || 0) + (documentCounts?.msaTemplates || 0) + (documentCounts?.coverPageTemplates || 0)}
              </div>
              <p className="text-xs text-muted-foreground">Terms + MSA + Cover Page</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardDescription>Support Documents</CardDescription>
              <Truck className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(documentCounts?.creditNotes || 0) + (documentCounts?.deliveryChallans || 0)}
              </div>
              <p className="text-xs text-muted-foreground">Credit Notes + Challans</p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Document Breakdown */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Core Business Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Core Business Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-blue-600" />
                  <span>Invoices</span>
                </div>
                <Badge variant="secondary">{documentCounts?.invoices || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-green-600" />
                  <span>Order Forms</span>
                </div>
                <Badge variant="secondary">{documentCounts?.orderForms || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-purple-600" />
                  <span>Customers</span>
                </div>
                <Badge variant="secondary">{documentCounts?.customers || 0}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Templates & Resources
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <FileCheck className="h-4 w-4 text-blue-600" />
                  <span>Terms Templates</span>
                </div>
                <Badge variant="secondary">{documentCounts?.termsTemplates || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-600" />
                  <span>MSA Templates</span>
                </div>
                <Badge variant="secondary">{documentCounts?.msaTemplates || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-purple-600" />
                  <span>Cover Pages</span>
                </div>
                <Badge variant="secondary">{documentCounts?.coverPageTemplates || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-orange-600" />
                  <span>Item Repository</span>
                </div>
                <Badge variant="secondary">{documentCounts?.itemRepository || 0}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Support & Operations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Support & Operations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-red-600" />
                  <span>Credit Notes</span>
                </div>
                <Badge variant="secondary">{documentCounts?.creditNotes || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-blue-600" />
                  <span>Delivery Challans</span>
                </div>
                <Badge variant="secondary">{documentCounts?.deliveryChallans || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Signature className="h-4 w-4 text-green-600" />
                  <span>E-Signatures</span>
                </div>
                <Badge variant="secondary">{documentCounts?.eSignatureRequests || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-purple-600" />
                  <span>Payment Transactions</span>
                </div>
                <Badge variant="secondary">{documentCounts?.paymentTransactions || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-orange-600" />
                  <span>Purchase Orders</span>
                </div>
                <Badge variant="secondary">{documentCounts?.purchaseOrders || 0}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Document Distribution Pie Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Document Distribution
                </CardTitle>
                <CardDescription>
                  Breakdown of all document types created by this user
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Template Usage Bar Chart */}
          {templateData.some(item => item.count > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Template Usage
                </CardTitle>
                <CardDescription>
                  Number of templates created by category
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={templateData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Activity Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity Summary
            </CardTitle>
            <CardDescription>
              Overall activity analysis for {user.fullName}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{documentCounts?.totalDocuments || 0}</div>
                <div className="text-sm text-blue-600">Total Documents Created</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {user.emailConfirmed ? 'Verified' : 'Pending'}
                </div>
                <div className="text-sm text-green-600">Account Status</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 capitalize">
                  {user.userType?.replace('_', ' ') || 'Unknown'}
                </div>
                <div className="text-sm text-purple-600">User Role</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </>
  );
}


